// GraphQL Client for RSS Aggregator
import { mockGraphQLClient } from './mockClient';
import { dynamicGraphQLClient } from './dynamicClient';

class GraphQLClient {
  constructor(endpoint, apiKey) {
    this.endpoint = endpoint;
    this.apiKey = apiKey;
    const useRealGraphQL = process.env.REACT_APP_USE_REAL_GRAPHQL === 'true';
    this.useMock = !useRealGraphQL && (!endpoint || endpoint === '' || process.env.NODE_ENV === 'development');
  }

  async query(query, variables = {}) {
    if (this.useMock) {
      console.log('Using mock GraphQL client');
      return mockGraphQLClient.query(query, variables);
    }

    console.log('Using real GraphQL API:', this.endpoint);

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.apiKey,
        },
        body: JSON.stringify({
          query,
          variables,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      return result.data;
    } catch (error) {
      console.error('GraphQL query error:', error);
      throw error;
    }
  }

  subscribe(subscription, variables = {}, callback) {
    console.log('Subscription not implemented in this demo');
  }
}

// GraphQL Queries
export const GET_ARTICLES = `
  query GetArticles(
    $limit: Int
    $nextToken: String
    $site_name: String
    $tag: String
    $search: String
    $sort_by: String
  ) {
    getArticles(
      limit: $limit
      nextToken: $nextToken
      site_name: $site_name
      tag: $tag
      search: $search
      sort_by: $sort_by
    ) {
      items {
        id
        title
        description
        link
        published
        author
        site_name
        tags
        content_snippet
      }
      nextToken
      total
    }
  }
`;

export const SEARCH_ARTICLES = `
  query SearchArticles($query: String!, $limit: Int, $nextToken: String) {
    searchArticles(query: $query, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        description
        link
        published
        author
        site_name
        tags
        content_snippet
      }
      nextToken
      total
    }
  }
`;

export const GET_ARTICLES_BY_DATE_RANGE = `
  query GetArticlesByDateRange(
    $start_date: String!
    $end_date: String!
    $limit: Int
    $nextToken: String
  ) {
    getArticlesByDateRange(
      start_date: $start_date
      end_date: $end_date
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        title
        description
        link
        published
        author
        site_name
        tags
        content_snippet
      }
      nextToken
      total
    }
  }
`;

export const GET_TAGS = `
  query GetTags {
    getTags
  }
`;

export const GET_FEEDS = `
  query GetFeeds {
    getFeeds {
      url
      site_name
      last_updated
      article_count
      most_recent_article_date
      status
    }
  }
`;

// GraphQL Mutations
export const ADD_FEED = `
  mutation AddFeed($url: String!, $site_name: String!) {
    addFeed(url: $url, site_name: $site_name) {
      url
      site_name
      last_updated
      article_count
      status
    }
  }
`;

export const REMOVE_FEED = `
  mutation RemoveFeed($url: String!) {
    removeFeed(url: $url)
  }
`;

export const UPDATE_FEED = `
  mutation UpdateFeed($old_url: String!, $new_url: String!, $new_site_name: String!) {
    updateFeed(old_url: $old_url, new_url: $new_url, new_site_name: $new_site_name) {
      url
      site_name
      last_updated
      article_count
      most_recent_article_date
      status
    }
  }
`;

export const REFRESH_FEEDS = `
  mutation RefreshFeeds {
    refreshFeeds
  }
`;

// Create client instance
const GRAPHQL_ENDPOINT = process.env.REACT_APP_GRAPHQL_ENDPOINT || '';
const GRAPHQL_API_KEY = process.env.REACT_APP_GRAPHQL_API_KEY || '';

// For production domains, use dynamic client that fetches API key from Lambda
// This ensures the API key is always current and handles key rotation automatically
const usesDynamicClient = window.location.hostname === 'cloudwatch.info' || 
                         window.location.hostname.includes('cloudfront.net');

console.log('GraphQL Client - Uses dynamic client:', usesDynamicClient);

export const graphqlClient = usesDynamicClient 
  ? dynamicGraphQLClient 
  : new GraphQLClient(GRAPHQL_ENDPOINT, GRAPHQL_API_KEY);

export default GraphQLClient;
